/**
 * ReceiptViewerDialog
 *
 * Full-screen overlay dialog for viewing receipt images/PDFs with zoom and pan.
 * Replaces the previous `window.open()` pattern. Uses shadcn Dialog, CSS
 * transform for zoom, and mouse/touch handlers for pan.
 *
 * Supports images (JPEG, PNG, WebP, HEIC) and PDFs via <iframe>.
 */
import { useState, useEffect, useCallback, useRef, type RefObject } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, ZoomIn, ZoomOut, RotateCcw, FileText, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import api, { endpoints } from '@/services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReceiptViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receiptId: string | null;
  returnFocusRef?: RefObject<HTMLElement | null>;
  metadata?: {
    filename?: string;
    size?: number;
    mimetype?: string;
    uploadedAt?: string;
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function formatMimeType(mime: string): string {
  const map: Record<string, string> = {
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/webp': 'WebP',
    'image/heic': 'HEIC',
    'application/pdf': 'PDF',
  };
  return map[mime] ?? mime;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ReceiptViewerDialog({
  open,
  onOpenChange,
  receiptId,
  returnFocusRef,
  metadata,
}: ReceiptViewerDialogProps) {
  // Blob state
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectedMime, setDetectedMime] = useState<string | null>(null);

  // Zoom & pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const panStart = useRef({ x: 0, y: 0 });
  const wasOpenRef = useRef(open);

  const isPdf =
    (metadata?.mimetype ?? detectedMime) === 'application/pdf';

  const closeViewer = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      requestAnimationFrame(() => returnFocusRef?.current?.focus());
    }
    wasOpenRef.current = open;
  }, [open, returnFocusRef]);

  // Fetch blob when dialog opens
  useEffect(() => {
    if (!open || !receiptId) {
      setBlobUrl(null);
      setError(null);
      setDetectedMime(null);
      return;
    }

    let cancelled = false;
    let url: string | null = null;

    setLoading(true);
    setError(null);

    api
      .get(endpoints.receipts.file(receiptId), { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const blob = res.data as Blob;
        setDetectedMime(blob.type);
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { error?: string } } })?.response?.data
            ?.error ??
          (err instanceof Error ? err.message : 'Could not load receipt');
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, receiptId]);

  // Reset zoom/pan when dialog opens/closes or receipt changes
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [open, receiptId]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const handleReset = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Mouse wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => {
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      return Math.min(Math.max(z + delta, MIN_ZOOM), MAX_ZOOM);
    });
  }, []);

  // Mouse drag for pan
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom <= 1) return;
      e.preventDefault();
      setIsDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY };
      panStart.current = { ...pan };
    },
    [zoom, pan],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setPan({
        x: panStart.current.x + dx,
        y: panStart.current.y + dy,
      });
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch support for pan
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (zoom <= 1 || e.touches.length !== 1) return;
      const touch = e.touches[0];
      setIsDragging(true);
      dragStart.current = { x: touch.clientX, y: touch.clientY };
      panStart.current = { ...pan };
    },
    [zoom, pan],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return;
      const touch = e.touches[0];
      const dx = touch.clientX - dragStart.current.x;
      const dy = touch.clientY - dragStart.current.y;
      setPan({
        x: panStart.current.x + dx,
        y: panStart.current.y + dy,
      });
    },
    [isDragging],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const effectiveMime = metadata?.mimetype ?? detectedMime;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="receipt-viewer-dialog"
        className="max-w-4xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0 overflow-hidden"
        onPointerDownOutside={(e) => {
          e.preventDefault();
          e.stopPropagation();
          closeViewer();
        }}
        onInteractOutside={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          closeViewer();
        }}
      >
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            {isPdf ? (
              <FileText className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ImageIcon className="h-4 w-4 text-muted-foreground" />
            )}
            {metadata?.filename ?? 'Receipt'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Receipt viewer with zoom and pan controls
          </DialogDescription>
        </DialogHeader>

        {/* Content area */}
        <div className="flex-1 min-h-0 relative bg-muted/30">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center" data-testid="receipt-viewer-loading">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center p-4" data-testid="receipt-viewer-error">
              <p className="text-sm text-destructive text-center">{error}</p>
            </div>
          )}

          {blobUrl && !loading && !error && (
            <>
              {isPdf ? (
                <iframe
                  src={blobUrl}
                  className="w-full h-full border-0"
                  title="Receipt PDF"
                  data-testid="receipt-viewer-pdf"
                />
              ) : (
                <div
                  className={cn(
                    'w-full h-full overflow-hidden flex items-center justify-center',
                    zoom > 1 && 'cursor-grab',
                    isDragging && 'cursor-grabbing',
                  )}
                  onWheel={handleWheel}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  data-testid="receipt-viewer-image-container"
                >
                  <img
                    src={blobUrl}
                    alt={metadata?.filename ?? 'Receipt'}
                    className="max-w-full max-h-full object-contain select-none"
                    style={{
                      transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                      transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                    }}
                    draggable={false}
                    data-testid="receipt-viewer-image"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer: Zoom controls + metadata */}
        <div className="shrink-0 border-t bg-background" data-testid="receipt-viewer-footer">
          {/* Zoom controls (hidden for PDF) */}
          {!isPdf && blobUrl && (
            <div className="flex items-center justify-center gap-2 py-2 border-b">
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={handleZoomOut}
                disabled={zoom <= MIN_ZOOM}
                aria-label="Zoom out"
                data-testid="receipt-viewer-zoom-out"
              >
                <ZoomOut className="h-3.5 w-3.5" />
              </Button>
              <span
                className="text-xs font-medium tabular-nums w-14 text-center"
                data-testid="receipt-viewer-zoom-level"
              >
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={handleZoomIn}
                disabled={zoom >= MAX_ZOOM}
                aria-label="Zoom in"
                data-testid="receipt-viewer-zoom-in"
              >
                <ZoomIn className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2"
                onClick={handleReset}
                aria-label="Reset zoom"
                data-testid="receipt-viewer-zoom-reset"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Reset
              </Button>
            </div>
          )}

          {/* Metadata */}
          <div className="px-4 py-2.5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
            {metadata?.filename && (
              <div>
                <span className="font-medium text-foreground">File: </span>
                <span className="break-all">{metadata.filename}</span>
              </div>
            )}
            {metadata?.size != null && (
              <div>
                <span className="font-medium text-foreground">Size: </span>
                {formatFileSize(metadata.size)}
              </div>
            )}
            {effectiveMime && (
              <div>
                <span className="font-medium text-foreground">Format: </span>
                {formatMimeType(effectiveMime)}
              </div>
            )}
            {metadata?.uploadedAt && (
              <div>
                <span className="font-medium text-foreground">Uploaded: </span>
                {new Date(metadata.uploadedAt).toLocaleDateString()}
              </div>
            )}
            <div>
              <span className="font-medium text-foreground">Max size: </span>
              10 MB
            </div>
            <div>
              <span className="font-medium text-foreground">Allowed: </span>
              JPEG, PNG, WebP, HEIC, PDF
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
